import type { ConversionParams } from './lib/conversionEvents'

/** How the widget presents itself on the page. */
export type DisplayMode = 'drawer' | 'modal' | 'inline' | 'page'

/** Which booking journey the widget runs. */
export type FlowType = 'general' | 'tyres' | 'mot'

/** What the customer pays at the point of booking. */
export type PaymentMode = 'none' | 'deposit' | 'full'

export const DISPLAY_MODES: readonly DisplayMode[] = ['drawer', 'modal', 'inline', 'page']
export const FLOW_TYPES: readonly FlowType[] = ['general', 'tyres', 'mot']
export const PAYMENT_MODES: readonly PaymentMode[] = ['none', 'deposit', 'full']

export function isDisplayMode(value: unknown): value is DisplayMode {
  return typeof value === 'string' && (DISPLAY_MODES as readonly string[]).includes(value)
}

export function isFlowType(value: unknown): value is FlowType {
  return typeof value === 'string' && (FLOW_TYPES as readonly string[]).includes(value)
}

export function isPaymentMode(value: unknown): value is PaymentMode {
  return typeof value === 'string' && (PAYMENT_MODES as readonly string[]).includes(value)
}

export interface BookingWidgetConfig {
  /** Base URL of the AutoChain API (e.g. https://app.example.com/api) */
  apiUrl: string
  /** Public key for the booking widget channel */
  publicKey: string
  /** Optional garage/location ID to pre-select */
  garageId?: string
  /** Optional CSS selector for the container. Defaults to the element passed to mount(). */
  containerSelector?: string

  /** Optional fallback duration, used only if service durations are missing */
  defaultDurationMinutes?: number

  /**
   * Overrides the channel's configured display mode. Lets one garage run a
   * drawer on the homepage and an inline block on /book from the same channel.
   */
  displayMode?: DisplayMode

  /**
   * Overrides the channel's configured journey, for the same reason: a garage
   * with one channel can put the tyres-only journey on their tyres page and the
   * full one on the homepage, without configuring two of everything.
   *
   * Only the order of the questions changes. Which services are sold, what is
   * charged and where the booking lands all stay with the channel.
   */
  flowType?: FlowType

  /**
   * Selector for an existing element on the host page that should open the
   * widget, instead of rendering our floating launcher.
   */
  triggerSelector?: string

  /** Set false to suppress the floating launcher entirely. */
  launcher?: boolean

  /**
   * Where this visit came from, for the garage's analytics and bookings funnel
   * (ACA-107). The hosted booking page sets it from its link's utm_source.
   * When absent the widget reads utm_source from the page it is on.
   */
  trafficSource?: string

  /**
   * The exact page to bring the customer back to after paying, for a widget
   * the site builder puts inside a frame (ACA-111). Set from the install
   * code's `data-autochain-return-url`. Used only when it is on one of the
   * channel's websites; otherwise a garage with a hosted booking page finishes
   * on that page's confirmation.
   */
  returnUrl?: string
}

/** Events the host page can subscribe to via `AutoChainBookingWidget.on()`. */
export interface WidgetEventMap {
  open: undefined
  close: undefined
  step: { step: string; index: number }
  vehicle_looked_up: { registration: string; found: boolean }
  services_selected: { serviceIds: string[]; total: number }
  slot_selected: { startTime: string }
  booking_created: { bookingId: string; reference: string }
  payment_completed: { bookingId: string }
  error: { message: string; stage: string }
  /**
   * A funnel step, once per visit (ACA-107): the same anonymous event the
   * widget gives Google Tag Manager and Google Analytics.
   */
  conversion: { event: string; params: ConversionParams }
}

export type WidgetEventName = keyof WidgetEventMap
